"use client";

import { EIP1193DerivedWallet } from "@aptos-labs/derived-wallet-ethereum";
import { useTranslations } from "next-intl";
import { useCallback } from "react";

import type { EvmChainConfig } from "@/components/evm-chain-selector-menu";
import { toast } from "@/components/toast";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { switchEvmNetwork } from "@/utils/evm-network-switch";

/**
 * Hook to switch the connected EVM wallet to a target chain.
 *
 * - No-ops (returns true) when:
 *   - there is no connected wallet, or
 *   - the wallet is not an EVM-derived wallet, or
 *   - no target chain config is provided.
 * - On failure, shows a localized error toast and disconnects the wallet.
 */
export function useSwitchEvmNetwork() {
  const { wallet, disconnect } = useDecibelWallet();
  const t = useTranslations("walletSelector");
  const tGeneral = useTranslations("general");

  const handleEvmNetworkSwitch = useCallback(
    async (targetChain: EvmChainConfig) => {
      if (!wallet || !(wallet instanceof EIP1193DerivedWallet)) {
        return true; // Not EVM wallet or no target, nothing to switch
      }

      try {
        // Cast provider to our interface - the actual provider has a compatible request method
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
        await switchEvmNetwork(wallet.eip1193Provider as any, targetChain);
        return true;
      } catch {
        toast({
          title: tGeneral("error"),
          description: t("networkSwitchFailed"),
          variant: "error",
        });
        disconnect();
        return false;
      }
    },
    [wallet, disconnect, t, tGeneral],
  );

  return { switchNetwork: handleEvmNetworkSwitch };
}
